import type { Infer } from 'spacetimedb';
import { AppearanceDefinitionsRow, DbConnection, GameItemsRow, MobDefinitionsRow, SkillDefinitionsRow, reducers } from '../generated';
import type { ConnectionManager } from '../core/ConnectionManager';
import type { ReducerClient } from '../core/ReducerClient';
import type { SubscriptionFactory, TableLike } from '../core/SubscriptionFactory';
import type {
  CatalogSnapshot,
  ItemCatalogEntry,
  SkillCatalogEntry,
  MobCatalogEntry,
  AppearanceCatalogEntry,
  Logger,
} from '../types';

type GameItemsTableRow = Infer<typeof GameItemsRow>;
type SkillDefinitionsTableRow = Infer<typeof SkillDefinitionsRow>;
type MobDefinitionsTableRow = Infer<typeof MobDefinitionsRow>;
type AppearanceDefinitionsTableRow = Infer<typeof AppearanceDefinitionsRow>;

interface CatalogSourceConfig<TRow, TEntry> {
  readonly key: string;
  readonly sql: string;
  readonly getTable: (db: DbConnection['db']) => TableLike<TRow>;
  readonly mapRow: (row: TRow) => TEntry;
  readonly sortEntries: (entries: TEntry[]) => TEntry[];
}

export class CatalogModule {
  private readonly cache = new Map<string, CatalogSnapshot<unknown>>();

  constructor(
    private readonly connectionManager: ConnectionManager,
    private readonly reducerClient: ReducerClient,
    private readonly subscriptionFactory: SubscriptionFactory,
    private readonly logger: Logger = () => undefined
  ) {}

  public clearCache(catalogKey?: string): void {
    if (catalogKey) {
      this.cache.delete(catalogKey);
      return;
    }
    this.cache.clear();
  }

  public getCachedItemCatalog(): CatalogSnapshot<ItemCatalogEntry> | null {
    const cached = this.cache.get('items');
    return cached ? cached as CatalogSnapshot<ItemCatalogEntry> : null;
  }

  public getCachedMobCatalog(): CatalogSnapshot<MobCatalogEntry> | null {
    const cached = this.cache.get('mobs');
    return cached ? cached as CatalogSnapshot<MobCatalogEntry> : null;
  }

  public getCachedAppearanceCatalog(): CatalogSnapshot<AppearanceCatalogEntry> | null {
    const cached = this.cache.get('appearances');
    return cached ? cached as CatalogSnapshot<AppearanceCatalogEntry> : null;
  }

  public async syncItemCatalog(options?: { forceRefresh?: boolean }): Promise<CatalogSnapshot<ItemCatalogEntry>> {
    if (!options?.forceRefresh) {
      const cached = this.cache.get('items');
      if (cached) {
        return cached as CatalogSnapshot<ItemCatalogEntry>;
      }
    }

    await this.authorizeItemCatalogAccess();
    const snapshot = await this.syncCatalog<GameItemsTableRow, ItemCatalogEntry>({
      key: 'items',
      sql: 'SELECT * FROM game_items',
      getTable: (db) => db.gameItems as unknown as TableLike<GameItemsTableRow>,
      mapRow: (row) => ({
        itemId: row.itemId,
        displayName: row.displayName,
        maxStack: Number(row.maxStack ?? 0),
        itemType: row.itemType,
        inCirculation: Number(row.inCirculation ?? 0),
        metadata: this.parseJson<Record<string, unknown>>(row.metadataJson, {}),
      }),
      sortEntries: (entries) => entries.sort((a, b) => a.itemId.localeCompare(b.itemId)),
    });

    this.cache.set('items', snapshot);
    return snapshot;
  }

  public async syncSkillCatalog(options?: { forceRefresh?: boolean }): Promise<CatalogSnapshot<SkillCatalogEntry>> {
    if (!options?.forceRefresh) {
      const cached = this.cache.get('skills');
      if (cached) {
        return cached as CatalogSnapshot<SkillCatalogEntry>;
      }
    }

    const snapshot = await this.syncCatalog<SkillDefinitionsTableRow, SkillCatalogEntry>({
      key: 'skills',
      sql: 'SELECT * FROM skill_definitions',
      getTable: (db) => db.skillDefinitions as unknown as TableLike<SkillDefinitionsTableRow>,
      mapRow: (row) => ({
        skillId: row.skillId,
        displayName: row.displayName,
        description: row.description,
        cooldownTicks: Number(row.cooldownTicks ?? 0),
        staminaCost: Number((row as SkillDefinitionsTableRow & { staminaCost?: number | null }).staminaCost ?? 0),
        range: Number(row.range ?? 0),
        damage: Number(row.damage ?? 0),
        emoji: row.emoji,
        inputsJson: row.inputsJson,
        tags: Array.isArray(row.tags)
          ? row.tags.filter((tag): tag is string => typeof tag === 'string')
          : [],
      }),
      sortEntries: (entries) => entries.sort((a, b) => a.skillId.localeCompare(b.skillId)),
    });

    this.cache.set('skills', snapshot);
    return snapshot;
  }

  public async syncMobCatalog(options?: { forceRefresh?: boolean }): Promise<CatalogSnapshot<MobCatalogEntry>> {
    if (!options?.forceRefresh) {
      const cached = this.cache.get('mobs');
      if (cached) {
        return cached as CatalogSnapshot<MobCatalogEntry>;
      }
    }

    const snapshot = await this.syncCatalog<MobDefinitionsTableRow, MobCatalogEntry>({
      key: 'mobs',
      sql: 'SELECT * FROM mob_definitions',
      getTable: (db) => db.mobDefinitions as unknown as TableLike<MobDefinitionsTableRow>,
      mapRow: (row) => ({
        mobTypeId: Number(row.mobTypeId ?? 0),
        entityType: row.entityType,
        displayName: row.displayName,
        baseHp: Number(row.baseHp ?? 0),
        baseMana: Number(row.baseMana ?? 0),
        moveDistance: Number(row.moveDistance ?? 0),
        speed: Number(row.speed ?? 0),
      }),
      sortEntries: (entries) => entries.sort((a, b) => a.mobTypeId - b.mobTypeId),
    });

    this.cache.set('mobs', snapshot);
    return snapshot;
  }

  public async syncAppearanceCatalog(options?: { forceRefresh?: boolean }): Promise<CatalogSnapshot<AppearanceCatalogEntry>> {
    if (!options?.forceRefresh) {
      const cached = this.cache.get('appearances');
      if (cached) {
        return cached as CatalogSnapshot<AppearanceCatalogEntry>;
      }
    }

    const snapshot = await this.syncCatalog<AppearanceDefinitionsTableRow, AppearanceCatalogEntry>({
      key: 'appearances',
      sql: 'SELECT * FROM appearance_definitions',
      getTable: (db) => db.appearanceDefinitions as unknown as TableLike<AppearanceDefinitionsTableRow>,
      mapRow: (row) => ({
        entityType: row.entityType,
        displayName: row.displayName,
      }),
      sortEntries: (entries) => entries.sort((a, b) => a.displayName.localeCompare(b.displayName) || a.entityType.localeCompare(b.entityType)),
    });

    this.cache.set('appearances', snapshot);
    return snapshot;
  }

  private async syncCatalog<TRow, TEntry>(
    config: CatalogSourceConfig<TRow, TEntry>
  ): Promise<CatalogSnapshot<TEntry>> {
    const entries = await this.subscriptionFactory.runOneTimeQuery({
      sql: config.sql,
      getTable: config.getTable,
      mapRow: config.mapRow,
    });

    const sortedEntries = config.sortEntries([...entries]);
    const version = this.computeCatalogVersion(config.key, sortedEntries);
    return {
      key: config.key,
      version,
      loadedAt: new Date(),
      entries: sortedEntries,
    };
  }

  private async authorizeItemCatalogAccess(): Promise<void> {
    const playerId = this.connectionManager.getPlayerId();
    if (!playerId) {
      this.logger('catalog_item_access_skipped_missing_player_id');
      return;
    }

    await this.reducerClient.callAndConfirm({
      reducerName: 'verify_item_definition_access',
      paramsType: reducers.verifyItemDefinitionAccess.paramsType,
      args: { playerId, itemIds: [] },
      onEvent: 'onVerifyItemDefinitionAccess',
      offEvent: 'removeOnVerifyItemDefinitionAccess',
      matchArgs: (args) => args.playerId === playerId,
      timeoutMessage: 'Item catalog authorization timed out',
      failureMessage: 'Item catalog authorization failed',
    });
  }

  private computeCatalogVersion<TEntry>(key: string, entries: readonly TEntry[]): string {
    const payload = JSON.stringify(entries);
    const hash = this.fnv1a(payload);
    return `${key}:${entries.length}:${hash}`;
  }

  private fnv1a(input: string): string {
    let hash = 0x811c9dc5;
    for (let i = 0; i < input.length; i += 1) {
      hash ^= input.charCodeAt(i);
      hash = Math.imul(hash, 0x01000193) >>> 0;
    }
    return hash.toString(16).padStart(8, '0');
  }

  private parseJson<T>(source: string | null | undefined, fallback: T): T {
    if (!source) {
      return fallback;
    }

    try {
      return JSON.parse(source) as T;
    } catch (error) {
      this.logger('catalog_json_parse_failure', {
        error: (error as Error).message,
        sourceSample: source.slice(0, 64),
      });
      return fallback;
    }
  }
}
